// The nuclear alert: a card top centre for every nuclear warhead in the air, so a launch is never
// missed in the noise of a raid. Each says what is coming and from where (a submarine out to sea, or
// over the map's edge, and on which bearing), what it will come down on, a clock and a fuse bar
// running down, and the live odds of stopping it from the THAADs that can still get an interceptor
// there in time, as the sim estimates them. When it is intercepted or goes off the card says so and
// slides away.
import { DEFAULT_CONFIG, TICKS_PER_SECOND, TILE, type CombatEvent, type WarheadInbound } from '@thrash/sim';

const el = document.getElementById('nuke-alert')!;
/** The combat feed sits where the cards go: it moves down out from under them while any are up. */
const feed = document.getElementById('feed')!;

/** How long a finished card stays up, so the outcome can be read. */
const LINGER_MS = 1600;
/** Most warheads given a full card at once; any more are counted on one line under them. */
const MAX_CARDS = 2;

interface Card { row: HTMLElement; clock: HTMLElement; fuse: HTMLElement; pk: HTMLElement; who: HTMLElement; done: boolean; left: number }
/** The line under the cards for the warheads past MAX_CARDS. */
const more = document.createElement('div');
more.className = 'nuke-more';
const cards = new Map<number, Card>();

const unitName = (t: string): string => (DEFAULT_CONFIG.units[t]?.name ?? t).toUpperCase();
const byName = (t: string): string => (DEFAULT_CONFIG.buildings[t]?.name ?? t).toUpperCase();

/** The compass point the launch lies on from where the warhead comes down: y runs south on the map. */
function bearing(dx: number, dy: number): string {
  const names = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const a = Math.atan2(dx, -dy); // 0 north, clockwise
  return names[Math.round(((a + Math.PI * 2) % (Math.PI * 2)) / (Math.PI / 4)) % 8]!;
}

function origin(w: WarheadInbound): string {
  const dx = w.fromX - w.x, dy = w.fromY - w.y;
  const tiles = Math.round(Math.hypot(dx, dy) / TILE);
  const from = w.by ? unitName(w.by) : 'OVER THE MAP EDGE';
  return `${from} · ${tiles} TILES ${bearing(dx, dy)}`;
}

function make(w: WarheadInbound): Card {
  const row = document.createElement('div');
  row.className = 'nuke live';
  row.innerHTML = `<div class="sym">☢</div>
    <div class="head"><span class="title"></span><span class="clock"></span></div>
    <div class="from"></div>
    <div class="odds"><span class="pk"></span><span class="who"></span><div class="fuse"><i></i></div></div>`;
  row.querySelector('.title')!.textContent = unitName(w.type);
  const target = w.aim === 'hq' ? 'COMMAND CENTER' : byName(w.aim);
  const from = row.querySelector<HTMLElement>('.from')!;
  from.textContent = `FROM ${origin(w)}  →  `;
  const b = document.createElement('b');
  b.textContent = target;
  from.append(b);
  el.append(row);
  el.hidden = false;
  return { row, clock: row.querySelector('.clock')!, fuse: row.querySelector('.fuse i')!, pk: row.querySelector('.pk')!, who: row.querySelector('.who')!, done: false, left: w.left };
}

/** The warheads in the air as the latest snapshot has them; call every frame. */
export function nukeAlert(warheads: readonly WarheadInbound[]): void {
  for (const w of warheads) {
    let c = cards.get(w.id);
    if (!c) { c = make(w); cards.set(w.id, c); }
    if (c.done) continue;
    c.left = w.left;
    const secs = w.left / TICKS_PER_SECOND;
    c.clock.textContent = `T−${secs.toFixed(1)}s`;
    c.fuse.style.transform = `scaleX(${Math.max(0, Math.min(1, w.left / w.total))})`;
    const pct = Math.round(w.odds * 100);
    c.pk.textContent = w.shooters > 0 ? `INTERCEPT ${pct}%` : w.missed > 0 ? 'MISSED · NO SECOND SHOT' : 'NO INTERCEPTOR CAN REACH IT';
    c.pk.className = `pk ${pct >= 70 ? 'good' : pct >= 40 ? 'fair' : 'bad'}`;
    c.who.textContent = w.shooters > 0 ? `${w.shooters} ${w.shooters === 1 ? 'DEFENCE' : 'DEFENCES'} ENGAGING${w.missed > 0 ? ` · ${w.missed} MISSED` : ''}` : '';
  }
  limit();
  clearFeed();
}

/**
 * The cards never fill the screen: the soonest MAX_CARDS warheads get a full card, the rest one
 * line counting them and the next to land.
 */
function limit(): void {
  const waiting = [...cards.values()].filter(c => !c.done).sort((a, b) => a.left - b.left);
  // a phone has room for one; the second card on a wide screen is a compact row
  const max = window.innerWidth < 760 ? 1 : MAX_CARDS;
  waiting.forEach((c, i) => { c.row.hidden = i >= max; c.row.classList.toggle('compact', i > 0); });
  const rest = waiting.slice(max);
  if (rest.length === 0) { more.remove(); return; }
  more.textContent = `☢ +${rest.length} MORE NUCLEAR ${rest.length === 1 ? 'WARHEAD' : 'WARHEADS'} INBOUND · NEXT T−${(rest[0]!.left / TICKS_PER_SECOND).toFixed(1)}s`;
  el.append(more);
}

/** Keeps the feed below the cards: its top follows the bottom of the stack, or goes back where it was. */
function clearFeed(): void {
  if (el.hidden) { feed.style.top = ''; return; }
  const r = el.getBoundingClientRect();
  // only when they are in each other's way: on a wide screen the feed sits clear to the right
  const f = feed.getBoundingClientRect();
  if (f.left >= r.right + 8) { feed.style.top = ''; return; }
  feed.style.top = `${Math.round(r.bottom + 10)}px`;
}

/** The outcome, from the sim's events: intercepted, or it went off. */
export function nukeAlertEvents(events: readonly CombatEvent[]): void {
  for (const e of events) {
    if (e.id === undefined || (e.kind !== 'intercepted' && e.kind !== 'impact')) continue;
    const c = cards.get(e.id);
    if (!c || c.done) continue;
    finish(e.id, c, e.kind === 'intercepted');
  }
}

function finish(id: number, c: Card, stopped: boolean): void {
  c.done = true;
  c.row.hidden = false;
  c.row.classList.remove('live');
  c.row.classList.add('done', stopped ? 'safe' : 'hit');
  c.clock.textContent = stopped ? 'SPLASHED' : 'DETONATION';
  c.fuse.style.transform = 'scaleX(0)';
  c.pk.textContent = stopped ? 'WARHEAD DESTROYED' : 'WARHEAD WENT OFF';
  c.pk.className = `pk ${stopped ? 'good' : 'bad'}`;
  c.who.textContent = '';
  setTimeout(() => c.row.classList.add('out'), LINGER_MS);
  limit();
  setTimeout(() => { c.row.remove(); cards.delete(id); limit(); if (cards.size === 0) { el.hidden = true; clearFeed(); } }, LINGER_MS + 450);
}

/** A warhead gone from the snapshot without a word (the sim was scrubbed back, say): drop its card. */
export function nukeAlertPrune(ids: ReadonlySet<number>): void {
  for (const [id, c] of cards) if (!c.done && !ids.has(id)) { c.row.remove(); cards.delete(id); }
  limit();
  if (cards.size === 0) { el.hidden = true; clearFeed(); }
}

/** A new game: nothing in the air. */
export function resetNukeAlert(): void {
  cards.clear();
  el.replaceChildren();
  el.hidden = true;
  clearFeed();
}
